<template>
  <div :class="[{'smart-input_one-row': oneRow}, sizeClass]" class="smart-input" ref="wrapper">
    <div v-if="caption" class="smart-input__name">
      {{ caption }}
    </div>
    <div class="smart-input__wrapper">
      <flat-pickr
        v-model="flatPickrValue"
        v-bind="$attrs"
        :config="actualConfigFlatPickr"
        :disabled="disabled"
        :events="['onChange','onClose','onOpen','onReady', 'onKeyDown']"
        :placeholder="placeholder"
        class="smart-input__field"
        @on-ready="onReady"
        @on-change="onChange"
        @on-open="onOpen"
        @on-close="onClose"
        @on-key-down="onKeyDown"
      />

      <!-- @slot Слот кнопки справа от поля ввода. -->
      <slot />
    </div>
    <div v-if="error" class="smart-input__error-message">
      {{ error }}
    </div>
  </div>
</template>

<script>
import {Russian} from "flatpickr/dist/l10n/ru.js";

/**
 * Компонент для поля ввода даты с возможностью отображения ошибок.
 */
export default {
  inheritAttrs: false,
  props: {
    /**
     * Значение, которое надо передавать через v-model.
     */
    value: {
      type: [String, Date],
      default: null,
    },
    /**
     * Заголовок поля.
     */
    caption: {
      type: String,
      default: "",
    },
    /**
     * Содержимое для placeholder в input-элемент.
     */
    placeholder: {
      type: String,
      default: "",
    },
    /**
     * Вариант размера компонента.
     *
     * @values s, m, l.
     */
    size: {
      type: String,
      default: "m",
    },
    /**
     * Текст ошибки для поля.
     */
    error: {
      type: String,
      default: "",
    },
    /**
     * Опция для отключения возможности ввода в поле.
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Указывается конкретное значение, которое будет возвращено через `@input`, при условии если содержимое было стерто.
     */
    defaultIfEmpty: {
      type: [String, Date],
      default: null,
    },
    /**
     * Флаг для размещения подписи и поля ввода на одной строке.
     */
    oneRow: {
      type: Boolean,
      default: false,
    },
    /**
     * Опции для настройки компонента отображения дат/времени.
     */
    configFlatPickr: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      flatPickrInstance: null,
      // Отдельная копия значения для компонента,
      // которое будет по разному модифицироваться внутри flatPickr и потому не участвует для отслеживания снаружи.
      flatPickrValue: this.value,
      // Новое значение изначально соответствует входному, а после будет задаваться честным Date при корректном изменении и отправке.
      newValue: this.value,
      isOpen: false, // Флаг для определения открытого календаря. Если календарь открыт - изменения снаружи компонента не допускаются.
      savedConfigFlatPickr: this.configFlatPickr, // Сохраненный набор настроек flatPickr, который не должен меняться если календарь открыт.
    };
  },
  computed: {
    /**
     * @return {Object} Плоский объект с актуальным набором настроек для виджета календаря.
     */
    actualConfigFlatPickr() {
      return {
        allowInput: true,
        dateFormat: "d.m.Y H:i",
        enableTime: true,
        enableSeconds: false,
        time_24hr: true,
        minuteIncrement: 1,
        locale: Russian,
        ...this.savedConfigFlatPickr,
        onChange: this.onChange,
        onOpen: this.onOpen,
        onClose: this.onClose,
        onReady: this.onReady,
        onKeyDown: this.onKeyDown,
      };
    },
    /**
     * @return {String} Вернуть класс, применяемый для размера компонента.
     */
    sizeClass() {
      const sizeClasses = {
        "l": "smart-input_large",
        "m": "smart-input_medium",
        "s": "smart-input_small",
      };
      return _.get(sizeClasses, this.size, "smart-input_medium");
    },
  },
  watch: {
    /**
     * Наблюдение за оригинальным значением для его синхронизации со внутренним значением компонента и отправкой события `@change`.
     * В момент редактирования дат, когда календарь открыт, синхронизации не происходит.
     *
     * @param {String} val
     */
    value(val) {
      if (this.isOpen) {
        return;
      }
      const newVal = val || this.defaultIfEmpty;
      if (this.eqNewValue(newVal)) {
        return;
      }
      this.newValue = newVal;
      this.flatPickrValue = newVal;
      /**
       * Срабатывает при изменении значения со стороны родительского компонента.
       * @event change
       */
      this.$emit("change", newVal);
    },
    /**
     * Наблюдение за оригинальным значением для его синхронизации со внутренним значением компонента.
     * В момент редактирования дат, когда календарь открыт, синхронизации не происходит.
     *
     * @param {Object} newConfigFlatPickr
     */
    configFlatPickr(newConfigFlatPickr) {
      if (this.isOpen) {
        return;
      }
      this.savedConfigFlatPickr = newConfigFlatPickr;
    },
  },
  mounted() {
    document.addEventListener("click", this.handleClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.handleClickOutside);
  },
  methods: {
    /**
     * Обработка события `@on-ready` в библиотеке `flatpickr`, чтобы сохранить экземпляр календаря.
     *
     * @param {Object} instance
     */
    onReady(...[, , instance]) {
      this.flatPickrInstance = instance;
    },
    /**
     * Вызов метода для открытия календаря.
     */
    open() {
      this.flatPickrInstance.open();
    },
    close() {
      this.flatPickrInstance.close();
    },
    /**
     * Обработка события `@on-change` в библиотеке `flatpickr`, а не его обертки - компонента `vue-flatpickr-component`.
     *
     * Через него происходит наблюдение за новым значением (в виде объекта `Date`) для отправки через `@input`.
     * Значение типа `Date` извлекается из массива `selectedDates`, который при определенных опциях может содержать несколько дат
     * (в случае выбора диапазона), но т.к. общим случаем использования является выбор одной даты, то остальными сценариями пренебрегаем.
     * При этом необходимо скопировать значение даты, чтобы не сохранять ссылок на объект даты между компонентами.
     *
     * ВАЖНО! Это какое-то блядство, но при закрытии окна выбора даты,
     * в котором допускается ввод времени с секундами, пустое значение будет заменено на текущее.
     * https://github.com/flatpickr/flatpickr/issues/2040
     * Здесь фикс который позволяет это делать при закрытии пустого поля через Enter.
     *
     * @see https://flatpickr.js.org/events/#onchange
     * @param {Array<Date>} selectedDates
     * @param {String} dateStr
     * @param {Object} instance
     */
    onChange(selectedDates, dateStr, instance) {
      const newValue = ((selectedDates[0] instanceof Date) ? new Date(selectedDates[0].getTime()) : selectedDates[0]) || this.defaultIfEmpty;
      if (!this.eqNewValue(newValue)) {
        this.newValue = newValue;
        /**
         * Срабатывает при установке (вводе, выборе в календаре) значения в компоненте.
         * @event input
         */
        this.$emit("input", newValue);
      }

      if (dateStr === "") {
        instance.close();
      }
    },
    /**
     * Обработка события `@on-key-down` в библиотеке `flatpickr` для отслеживания случаев ввода дат по нажатию Enter.
     *
     * @param {Array<Date>} selectedDates
     * @param {String} dateStr
     * @param {Object} instance
     * @param {KeyboardEvent} event
     */
    onKeyDown(selectedDates, dateStr, instance, event) {
      if (event.key === "Enter") {
        this.onChange(selectedDates, dateStr, instance);
      }
    },
    /**
     * Вернет true если значения timestamp (в секундах) между аргументом и newValue равны (и == для "не дат" нормально).
     * Применяется чтобы избежать отправки бессмысленных событий.
     *
     * Примечание: если извне изменили время то оно может содержать миллисекунды, но при присвоении значений во flatpickr он их убирает.
     * Тем самым провоцируя новое изменение значения. Поэтому миллисекунды выбрасываем.
     *
     * @param {Date|*} otherValue
     */
    eqNewValue(otherValue) {
      if ((otherValue instanceof Date) && (this.newValue instanceof Date)) {
        return Math.trunc(otherValue.getTime() / 1000) === Math.trunc(this.newValue.getTime() / 1000);
      }
      // noinspection EqualityComparisonWithCoercionJS
      return otherValue == this.newValue;
    },
    // фикс сохранения даты при клике вне календаря
    handleClickOutside(event) {
      if (this.isOpen && !this.$refs.wrapper.contains(event.target) && !this.flatPickrInstance.calendarContainer.contains(event.target)) {
        this.flatPickrValue = this.newValue;
        this.close();
      }
    },
    onOpen() {
      this.isOpen = true;
    },
    onClose() {
      this.isOpen = false;
      this.flatPickrValue = this.newValue;
      this.$emit("input", this.newValue);
    },
  },
};
</script>

<style lang="scss">
@import "./scss/style.scss";
</style>
